// src/components/extensions/FoldDemo.tsx
//
// An inline, animated demonstration of what an extension actually does, rendered
// in the card rather than linked out to a recording.
//
// This one shows a styled-components block being folded: the gutter chevron
// turns, the body of the declaration collapses, and the lines it replaced are
// summarised the way the editor summarises them. It is the extension's single
// most important behaviour, and a screenshot of it says nothing where twelve
// lines of it in motion say everything.
//
// The demo plays itself once when the card scrolls into view, then the gutter
// control becomes a real toggle. That ordering matters: a visitor who never
// touches it still sees the feature happen, and one who does gets to try it.
//
// Two things are deliberately not animated here. Nothing loops — the demo
// settles into its folded state and stays there, because a fold that repeatedly
// folds itself is noise, and about/motion.ts rules out idle motion for the same
// reason. And nothing is autoplayed under reduced motion: the code is simply
// shown open, and the gutter folds it instantly on request.
//
// A note on the sample code below, since it looks like a mistake otherwise: the
// lines are ordinary double-quoted strings, not template literals. They contain
// backticks and ${...}, which would be evaluated as real interpolation inside a
// template literal. Quoted plainly, they display exactly as written.
import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { VscChevronRight, VscFileCode, VscFold, VscUnfold } from 'react-icons/vsc';

import { DURATION, EASE_OUT, motionSet } from '../about/motion';
import {
  CodeArea,
  CodeLine,
  CodeText,
  EditorFrame,
  FileKind,
  GutterSpacer,
  LineNumber,
  TabBar,
} from './editor.styles';
import {
  BoundaryText,
  Chevron,
  FoldableBody,
  FoldSummary,
  FoldSummaryText,
  FoldToggle,
  KeyCap,
  ShortcutLabel,
  ShortcutRow,
} from './FoldDemo.styles';

/* eslint-disable no-template-curly-in-string -- The ${...} below are sample code shown to the reader as text, not interpolations. Quoted as plain strings precisely so they render literally; that is what the rule mistakes for a mistake. */
/**
 * The sample file.
 *
 * Split into the parts around the foldable region rather than kept as one flat
 * list, because the two boundary lines are the region's handles: they stay
 * visible when it is folded, which is what leaves the reader able to tell what
 * was collapsed. That is the whole reason folding is useful, so a demo that hid
 * the signature would be missing the point.
 */
const SAMPLE = {
  fileName: 'Panel.styles.ts',
  /** Unfoldable context above the region. */
  before: ["import styled from 'styled-components';", ''],
  /** Opens the region, and stays visible. */
  open: 'const Panel = styled.section`',
  /** The lines that collapse away. */
  body: [
    '  display: flex;',
    '  flex-direction: column;',
    '  gap: 0.75rem;',
    '  padding: 1.5rem;',
    '  background: ${({ theme }) => theme.surface};',
    '  border: 1px solid ${({ theme }) => theme.borderColor};',
    '  border-radius: ${({ theme }) => theme.borderRadius.lg};',
  ],
  /** Closes the region, and stays visible. */
  close: '`;',
  after: ['', 'export default Panel;'],
} as const;
/* eslint-enable no-template-curly-in-string */

/** How long the code stays open before the demo folds it, in ms. */
const AUTO_FOLD_DELAY = 1500;

/** How long the keycaps read as pressed, in ms. */
const SHORTCUT_FLASH = 420;

const KEYCAPS = ['Ctrl', 'Alt', 'F'] as const;

/** Attaches a 1-based line number to each line of a run. */
const numbered = (lines: readonly string[], start: number) =>
  lines.map((text, i) => ({ text, lineNo: start + i }));

const FoldDemo = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  const frameRef = useRef<HTMLDivElement>(null);
  /**
   * Per-instance, not a literal. Two entries could both carry a demo, and a
   * hardcoded id would then be duplicated in the document — which silently
   * breaks aria-controls for the second one, pointing the toggle at the first.
   */
  const bodyId = `fold-demo-body-${useId()}`;

  const [folded, setFolded] = useState(false);
  /**
   * Whether the demo is the thing doing the folding, as opposed to the reader.
   * Only then do the keycaps press — a shortcut that animates when the code was
   * folded by clicking the gutter would be describing an action that did not
   * happen.
   */
  const [pressing, setPressing] = useState(false);

  /**
   * Once, and only while the demo is off screen. `amount: 0.5` so a card that is
   * barely peeking in does not start its animation before it can be seen.
   */
  const inView = useInView(frameRef, { once: true, amount: 0.5 });

  useEffect(() => {
    if (!inView || reduceMotion) return undefined;

    const foldTimer = window.setTimeout(() => {
      setPressing(true);
      setFolded(true);
    }, AUTO_FOLD_DELAY);

    const flashTimer = window.setTimeout(() => setPressing(false), AUTO_FOLD_DELAY + SHORTCUT_FLASH);

    return () => {
      window.clearTimeout(foldTimer);
      window.clearTimeout(flashTimer);
    };
  }, [inView, reduceMotion]);

  const { before, body, after } = SAMPLE;

  const beforeLines = numbered(before, 1);
  const openNo = before.length + 1;
  const bodyNo = openNo + 1;
  const closeNo = bodyNo + body.length;
  const afterLines = numbered(after, closeNo + 1);

  const toggle = () => setFolded((open) => !open);

  /** A plain, unnumbered line: no gutter control, so the number column is filled. */
  const renderPlain = ({ text, lineNo }: { text: string; lineNo: number }) => (
    <CodeLine key={`${lineNo}-${text}`}>
      <LineNumber>{lineNo}</LineNumber>
      <GutterSpacer aria-hidden="true" />
      <CodeText>{text}</CodeText>
    </CodeLine>
  );

  return (
    <EditorFrame ref={frameRef}>
      <TabBar>
        <VscFileCode aria-hidden="true" />
        <span>
          {SAMPLE.fileName.split('.', 1)[0]}.<FileKind>{SAMPLE.fileName.split('.').slice(1).join('.')}</FileKind>
        </span>
      </TabBar>

      <CodeArea>
        {beforeLines.map(renderPlain)}

        {/* The region's opening line, with the fold control in the gutter. */}
        <CodeLine>
          <LineNumber>{openNo}</LineNumber>
          <FoldToggle
            type="button"
            onClick={toggle}
            aria-expanded={!folded}
            aria-controls={bodyId}
            aria-label={`${folded ? 'Unfold' : 'Fold'} the styled component`}
          >
            <Chevron
              animate={{ rotate: folded ? 0 : 90 }}
              transition={{ duration: anim.micro, ease: EASE_OUT }}
            >
              <VscChevronRight aria-hidden="true" />
            </Chevron>
          </FoldToggle>
          <BoundaryText>{SAMPLE.open}</BoundaryText>
        </CodeLine>

        {/* The collapsible body. Animating to `height: 0` rather than unmounting
            the lines is what makes the collapse legible; the summary below then
            takes the space the body vacated. */}
        <FoldableBody
          id={bodyId}
          initial={false}
          animate={{ height: folded ? 0 : 'auto' }}
          transition={{ duration: anim.morph, ease: EASE_OUT }}
        >
          {numbered(body, bodyNo).map(renderPlain)}
        </FoldableBody>

        {/* The stand-in for the collapsed lines, in the editor's own idiom. */}
        <AnimatePresence initial={false}>
          {folded && (
            <FoldSummary
              key="fold-summary"
              onClick={toggle}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggle();
                }
              }}
              aria-label={`Unfold the ${body.length} folded lines`}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: DURATION.micro, ease: EASE_OUT }}
            >
              <VscFold aria-hidden="true" />
              <FoldSummaryText>
                {body.length} lines folded
              </FoldSummaryText>
              <VscUnfold aria-hidden="true" />
            </FoldSummary>
          )}
        </AnimatePresence>

        <CodeLine>
          <LineNumber>{closeNo}</LineNumber>
          <GutterSpacer aria-hidden="true" />
          <BoundaryText>{SAMPLE.close}</BoundaryText>
        </CodeLine>

        {afterLines.map(renderPlain)}
      </CodeArea>

      <ShortcutRow>
        <ShortcutLabel>Fold all</ShortcutLabel>
        {KEYCAPS.map((cap) => (
          <KeyCap
            key={cap}
            animate={pressing ? { y: 1 } : { y: 0 }}
            transition={{ duration: DURATION.microReduced, ease: EASE_OUT }}
          >
            {cap}
          </KeyCap>
        ))}
      </ShortcutRow>
    </EditorFrame>
  );
};

export default FoldDemo;
